import { useCallback, useEffect, useState } from "react";
import { ToolWithSidebar } from "@/components/ToolWithSidebar";
import { FileDropZone, ProcessingState, DownloadButton, ResultPreview } from "@/components/ToolLayout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import { trackToolCompleted } from "@/lib/analytics";
import { formatFileSize } from "@/lib/formatFileSize";
import { plural } from "@/lib/plural";
import { useRunToken } from "@/hooks/useRunToken";
import { TrialFooterNotice } from "@/components/TrialFooterNotice";
import { readDocx, NotDocxError } from "./docxRead";
import { blocksText, type DocxDoc } from "./docxModel";
import { convertDocxDocumentToPdf, DocxPdfCancelled } from "./docxToPdf";
import { convertTextToPdfDocument, decodeTextFile, type PageSizeName } from "./textToPdf";
import { browserRasterEnv, unsupportedScripts } from "./pdfFonts";
import { NoticeBox } from "@/components/NoticeBox";

const TOOL_INFO_CONTENT = {
  what: `Turns a Word document (.docx) or a plain text file (.txt) into a PDF. For a Word file the PDF keeps the headings, bold and italic, colours, bullet and numbered lists, tables (with borders, shading and repeating header rows), pictures, links, tabs, page breaks, headers and footers with page numbers, and the page size and margins of your document. Text only is also available if you want the plainest, smallest file.\n\nFor best results use a document saved from Word, Google Docs or LibreOffice. Fonts are matched to the closest standard PDF font (Calibri and Arial become Helvetica-style, Times New Roman and Cambria become Times-style), so line breaks can fall in slightly different places than in Word.`,
  how: `Drop a .docx or .txt file, choose Keep formatting or Text only, and click Convert to PDF. You see the page count and file size, a preview, and the PDF to download. Nothing is uploaded; it all runs in your browser.`,
  tip: `Convert as the last step, after you finish editing. Greek, Cyrillic and most European languages are supported. Chinese, Japanese, Korean, Hindi and other Indic scripts, Arabic, Hebrew, Thai and emoji are drawn as pictures so they look right, but that text cannot be selected or searched in the PDF. Text boxes, shapes, charts and multi-column layouts are reproduced by “Exact look” (pages as pictures with a hidden text layer), which is chosen for you when a file has them. Old .doc, .rtf and .odt files are not accepted: open them in Word and save as .docx first.`,
};

type Mode = "format" | "exact" | "text";
interface Loaded {
  file: File;
  kind: "docx" | "txt";
  doc?: DocxDoc;
  text: string;
  scripts: { name: string; count: number }[];
}
interface Result {
  blob: Blob;
  pages: number;
  warnings: string[];
  forFile: string;
}

const stem = (name: string) => name.replace(/\.[^.]+$/, "");

export default function WordToPdf() {
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [reading, setReading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<Result | null>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [mode, setMode] = useState<Mode>("format");
  const [pageSize, setPageSize] = useState<PageSizeName>(() => (typeof navigator !== "undefined" && /^en-(US|CA)|^es-MX/i.test(navigator.language) ? "Letter" : "A4"));
  const runs = useRunToken();

  useEffect(() => () => runs.cancel(), [runs]);

  const reset = () => {
    runs.cancel();
    setProcessing(false);
    setReading(false);
    setLoaded(null);
    setResult(null);
    setErrorMessage("");
    setProgress(0);
  };

  const handleFiles = useCallback(
    async (files: File[]) => {
      runs.cancel();
      setProcessing(false);
      setResult(null);
      setErrorMessage("");
      setLoaded(null);
      const file = files[0];
      if (!file) return;
      const lower = file.name.toLowerCase();
      if (/\.doc$/.test(lower)) return setErrorMessage("Old .doc files are not supported. Open it in Word and use Save As → Word Document (.docx), then drop it here.");
      if (/\.rtf$/.test(lower)) return setErrorMessage("RTF files are not supported. Open it in Word and save it as .docx, then drop it here.");
      if (/\.odt$/.test(lower)) return setErrorMessage("OpenDocument (.odt) files are not supported. Open it in Word or LibreOffice and save it as .docx, then drop it here.");
      const run = runs.start();
      setReading(true);
      try {
        if (lower.endsWith(".docx")) {
          const doc = await readDocx(file);
          if (!runs.isCurrent(run)) return;
          const text = doc.sections.map((s) => blocksText(s.blocks)).join("\n");
          setLoaded({ file, kind: "docx", doc, text, scripts: unsupportedScripts(text) });
          // Text boxes, charts and columns are only reproduced by the exact-look converter.
          const w = doc.warnings;
          setMode(w.textBoxes > 0 || w.charts > 0 || w.columnSections > 0 ? "exact" : "format");
        } else {
          const text = decodeTextFile(await file.arrayBuffer());
          if (!runs.isCurrent(run)) return;
          setLoaded({ file, kind: "txt", text, scripts: unsupportedScripts(text) });
        }
      } catch (err) {
        if (!runs.isCurrent(run)) return;
        console.error(err);
        setErrorMessage(
          err instanceof NotDocxError
            ? "This file is not a Word document (.docx). It may be damaged, or it may be another kind of file renamed to .docx."
            : "This file could not be read. It may be damaged or password-protected.",
        );
      } finally {
        if (runs.isCurrent(run)) setReading(false);
      }
    },
    [runs],
  );

  const convert = async () => {
    if (!loaded) return;
    if (!loaded.text.trim() && !loaded.doc?.sections.some((s) => s.blocks.length)) {
      setErrorMessage("There is no text in this file, so there is nothing to convert.");
      return;
    }
    const run = runs.start();
    setProcessing(true);
    setResult(null);
    setErrorMessage("");
    setProgress(0);
    try {
      const title = stem(loaded.file.name);
      const hooks = { isCurrent: () => runs.isCurrent(run), progress: (f: number) => runs.isCurrent(run) && setProgress(f * 100) };
      let bytes: Uint8Array, pages: number, warnings: string[];
      if (loaded.kind === "docx" && mode === "exact") {
        const { convertDocxToPdfRaster, DocxRasterCancelled } = await import("./docxRaster");
        try {
          const out = await convertDocxToPdfRaster(loaded.file, { fallbackTitle: title, title: loaded.doc?.title, author: loaded.doc?.author, isCurrent: hooks.isCurrent, progress: hooks.progress });
          bytes = out.bytes;
          pages = out.pages;
          warnings = [];
        } catch (e) {
          if (e instanceof DocxRasterCancelled) return;
          throw e;
        }
      } else if (loaded.kind === "docx" && mode === "format" && loaded.doc) {
        const out = await convertDocxDocumentToPdf(loaded.doc, { fallbackTitle: title }, hooks);
        ({ bytes, pages, warnings } = out);
      } else {
        const out = await convertTextToPdfDocument(loaded.text, { pageSize, title: loaded.doc?.title || title, isCurrent: hooks.isCurrent });
        ({ bytes, pages, warnings } = out);
      }
      if (!runs.isCurrent(run)) return;
      setResult({ blob: new Blob([bytes as BlobPart], { type: "application/pdf" }), pages, warnings, forFile: loaded.file.name });
      toast.success(`Converted to PDF (${plural(pages, "page")})`);
      trackToolCompleted("word-to-pdf", "pdf-tools");
    } catch (err) {
      if (err instanceof DocxPdfCancelled || !runs.isCurrent(run) || (err instanceof Error && err.message === "cancelled")) return;
      console.error("Word to PDF conversion failed:", err);
      setErrorMessage("The conversion failed. Try again, or choose Text only.");
    } finally {
      if (runs.isCurrent(run)) setProcessing(false);
    }
  };

  const CAN_PICTURE = browserRasterEnv() !== null;
  const hasFormatting = loaded?.kind === "docx";
  const warnScripts = loaded?.scripts ?? [];

  return (
    <ToolWithSidebar toolId="word-to-pdf" categoryId="pdf-tools" whatItDoes={TOOL_INFO_CONTENT.what} proTip={TOOL_INFO_CONTENT.tip}>
      <div className="mb-4 p-3 rounded-lg border bg-muted/50 text-xs text-muted-foreground flex flex-wrap gap-x-4 gap-y-1">
        <span>🔒 Runs entirely in your browser, never uploaded</span>
        <span>📄 Keeps headings, lists, tables, pictures and page numbers</span>
      </div>
      {errorMessage && (
        <div role="alert" className="mb-4 p-3 rounded-lg border border-destructive/40 bg-destructive/5 text-sm flex gap-2">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-destructive" />
          <span>{errorMessage}</span>
        </div>
      )}
      {!loaded ? (
        reading ? (
          <ProcessingState message="Reading the file…" onStop={reset} />
        ) : (
          <FileDropZone gateAtDownload onAttempt={() => setErrorMessage("")} accept=".docx,.txt" acceptLabel=".docx and .txt" onFiles={handleFiles} label="Drop a .docx or .txt file" description="Word documents (.docx) and plain text files (.txt)" />
        )
      ) : (
        <div className="space-y-4">
          <div className="p-4 rounded-lg border bg-card">
            <p className="text-sm font-medium break-all">{loaded.file.name}</p>
            <p className="text-xs text-muted-foreground">{formatFileSize(loaded.file.size)}</p>
          </div>

          {warnScripts.length > 0 && (
            <NoticeBox role="alert" className="text-sm flex gap-2">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
              <div>
                <p className="font-medium">
                  This file has {warnScripts.map((s) => `${s.name} (${plural(s.count, "character")})`).join(", ")}.
                </p>
                <p className="text-muted-foreground">{CAN_PICTURE ? "These are drawn as pictures so they look right, but that text cannot be selected, copied or searched in the PDF. Everything else stays real text." : "This browser cannot draw them, so they will appear as “?” in the PDF. Use Save as PDF in Word for a complete result."}</p>
              </div>
            </NoticeBox>
          )}
          {hasFormatting && loaded.doc && mode === "format" && (loaded.doc.warnings.textBoxes > 0 || loaded.doc.warnings.charts > 0 || loaded.doc.warnings.columnSections > 0 || loaded.doc.warnings.pictures > 0) && (
            <NoticeBox role="status" className="text-sm">
              Not carried over:{" "}
              {[
                loaded.doc.warnings.textBoxes > 0 && `${plural(loaded.doc.warnings.textBoxes, "text box / shape", "text boxes / shapes")}`,
                loaded.doc.warnings.charts > 0 && `${plural(loaded.doc.warnings.charts, "chart")}`,
                loaded.doc.warnings.pictures > 0 && `${plural(loaded.doc.warnings.pictures, "picture")} in an unsupported format`,
                loaded.doc.warnings.columnSections > 0 && `${plural(loaded.doc.warnings.columnSections, "multi-column section")} (shown as one column)`,
              ]
                .filter(Boolean)
                .join(", ")}
              . <button type="button" className="underline" onClick={() => setMode("exact")}>Use “Exact look” to include them</button>.
            </NoticeBox>
          )}

          {!processing && !result && (
            <fieldset className="space-y-3">
              <legend className="text-sm font-medium mb-1">Output</legend>
              {hasFormatting && (
                <div className="flex flex-wrap gap-2" role="group" aria-label="Conversion mode">
                  {([["format", "Keep formatting (selectable text)"], ["exact", "Exact look (pages as pictures)"], ["text", "Text only (smallest file)"]] as [Mode, string][]).map(([id, label]) => (
                    <Button key={id} size="sm" variant={mode === id ? "default" : "outline"} aria-pressed={mode === id} onClick={() => setMode(id)}>
                      {label}
                    </Button>
                  ))}
                </div>
              )}
              {(!hasFormatting || mode === "text") && (
                <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Page size">
                  <span className="text-sm text-muted-foreground">Page size</span>
                  {(["A4", "Letter"] as PageSizeName[]).map((p) => (
                    <Button key={p} size="sm" variant={pageSize === p ? "default" : "outline"} aria-pressed={pageSize === p} onClick={() => setPageSize(p)}>
                      {p === "Letter" ? "US Letter" : p}
                    </Button>
                  ))}
                </div>
              )}
              {hasFormatting && mode === "format" && <p className="text-xs text-muted-foreground">The page size and margins of your document are used. Text stays real, selectable text.</p>}
              {hasFormatting && mode === "text" && <p className="text-xs text-muted-foreground">Only the body text is kept: no pictures, colours, headers, footers or page numbers. Page breaks are kept.</p>}
              {hasFormatting && mode === "exact" && <p className="text-xs text-muted-foreground">Every page is drawn exactly as Word lays it out, including text boxes, shapes, charts and columns. Pages are pictures with a hidden text layer, so Latin-script text can still be searched; the file is larger and can take longer.</p>}
            </fieldset>
          )}

          {!result && !processing && <TrialFooterNotice toolId="word-to-pdf" />}

          <div className="flex flex-wrap gap-3">
            {processing ? (
              <div className="w-full">
                <ProcessingState message="Converting…" progress={progress} onStop={() => { runs.cancel(); setProcessing(false); }} />
              </div>
            ) : result ? (
              <>
                <DownloadButton blob={result.blob} fileName={`${stem(result.forFile)}.pdf`} />
                <Button variant="outline" onClick={reset}>Convert another file</Button>
              </>
            ) : (
              <>
                <Button onClick={convert} disabled={reading}>Convert to PDF</Button>
                <Button variant="outline" onClick={reset}>Choose a different file</Button>
              </>
            )}
          </div>

          {result && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground" role="status">
                {plural(result.pages, "page")} · {formatFileSize(result.blob.size)} · {stem(result.forFile)}.pdf
              </p>
              {result.warnings.map((w, i) => (
                <NoticeBox key={i} className="text-sm" role="alert">{w}</NoticeBox>
              ))}
              <ResultPreview blob={result.blob} mimeType="application/pdf" />
            </div>
          )}
        </div>
      )}
    </ToolWithSidebar>
  );
}
